/**
 * UNIT AWARD BUILDER — V2 (DIRECT DEVICE DROPDOWNS)
 * ------------------------------------------------
 * Same as the bundle builder, EXCEPT how devices are chosen. Instead of a
 * single "Times Awarded" dropdown that auto-calculates silver/bronze oak leaf
 * clusters, each ribbon now has TWO dropdowns:
 *   • Silver Oak Leaf Clusters (None–4)
 *   • Bronze Oak Leaf Clusters (None–4)
 * The combined total per ribbon is capped at 4 (the dropdowns limit each other).
 * Each ribbon stores entry.silver and entry.bronze directly.
 *
 * Everything else is unchanged from the current builder:
 *   • Every line item is stamped with the same `_Build ID` + `_Bundle Title`
 *     so the theme-only cart-clean script (or a cart transform function) can
 *     collapse the build into one clean cart line.
 *   • Devices charge the correct SET product (bronze 1–4, silver 1–2; silver
 *     3–4 fall back to singles) from the device_group metaobject / config.
 *   • Device-attachment FEE is per ribbon, per device type (any bronze = 1 fee,
 *     any silver = 1 fee).
 *   • Inventory deducts on rack/ribbons/devices; everything shows on the order.
 */
(function () {
  'use strict';

  let rackData, awardData, config;
  try { rackData = JSON.parse(document.getElementById('uab-rack-data').textContent); } catch (e) { console.error('UAB: rack parse', e); rackData = []; }
  try { awardData = JSON.parse(document.getElementById('uab-award-data').textContent); } catch (e) { console.error('UAB: award parse', e); awardData = []; }
  try { config = JSON.parse(document.getElementById('uab-config').textContent); } catch (e) { console.error('UAB: config parse', e); config = { assembly_fee_tiers: [], assembly_tier1_max: 6, assembly_tier2_max: 13, device_fee_variant_id: null, device_fee_price: 0, bronze_olc: null, silver_olc: null }; }

  function inferRackSize(p) { if (p.rack_size > 0) return p.rack_size; const m = p.title.toLowerCase().match(/(\d+)/); return m ? parseInt(m[1], 10) : 0; }
  rackData.forEach(r => { r.rack_size = inferRackSize(r); });

  const awardsSorted = [...awardData].sort((a, b) => a.precedence - b.precedence);
  // Multiple rack products can share the same size (tight vs spaced are separate products)
  const racksBySize = {};
  rackData.forEach(r => {
    if (r.rack_size > 0) {
      if (!racksBySize[r.rack_size]) racksBySize[r.rack_size] = [];
      racksBySize[r.rack_size].push(r);
    }
  });
  const availableSizes = Object.keys(racksBySize).map(Number).sort((a, b) => a - b);

  const AWARDS_PER_ROW = 3, MAX_DEVICES = 4;
  const bronzeOlc = config.bronze_olc;
  const silverOlc = config.silver_olc;

  // Set products from the device_group metaobject: { bronze:{1:{variant_id,price,title},...}, silver:{...} }
  const deviceSets = config.device_sets || { bronze: {}, silver: {} };
  function deviceSet(color, count) {
    const sets = (deviceSets && deviceSets[color]) || {};
    return sets[String(count)] || null;
  }
  // Price for `count` devices of a color: the matching SET product price; else
  // fall back to N× the single (qty-1) set; else N× the legacy single OLC.
  function devicePriceFor(color, count) {
    if (count <= 0) return 0;
    const set = deviceSet(color, count);
    if (set) return set.price;
    const single = deviceSet(color, 1);
    if (single) return single.price * count;
    const legacy = color === 'silver' ? silverOlc : bronzeOlc;
    return legacy ? legacy.price * count : 0;
  }
  // Cart line item(s) for `count` devices of a color on one award: 1× the set
  // product when available, else N× the single set, else the legacy OLC variant.
  function deviceCartItems(color, count, idx, awardTitle) {
    const out = [];
    if (count <= 0) return out;
    const label = color === 'silver' ? 'Silver Oak Leaf Cluster' : 'Bronze Oak Leaf Cluster';
    const set = deviceSet(color, count);
    if (set) {
      out.push({ id: set.variant_id, quantity: 1, properties: {
        '_Build ID': state.buildId, 'For Award': '#' + (idx + 1) + ' — ' + awardTitle,
        'Device': label + (count > 1 ? ' (set of ' + count + ')' : ''), 'Quantity': String(count)
      }});
    } else {
      const single = deviceSet(color, 1);
      const vid = single ? single.variant_id : (color === 'silver' ? (silverOlc && silverOlc.variant_id) : (bronzeOlc && bronzeOlc.variant_id));
      if (vid) out.push({ id: vid, quantity: count, properties: {
        '_Build ID': state.buildId, 'For Award': '#' + (idx + 1) + ' — ' + awardTitle,
        'Device': label, 'Quantity': String(count)
      }});
    }
    return out;
  }

  // ── Helpers ───────────────────────────────────────────
  function fmt(c) { return '$' + (c / 100).toFixed(2); }
  function genId() { return 'BLD-' + Date.now() + '-' + Math.random().toString(36).substring(2, 6).toUpperCase(); }
  function esc(s) { const d = document.createElement('div'); d.textContent = s; return d.innerHTML; }
  function findEntry(id) { return state.selectedAwards.find(e => e.award.id === id); }
  function isOlcEligible(award) { return award.device_groups && award.device_groups.length > 0; }

  // V2: device counts are chosen directly per ribbon (no Times Awarded math).
  function getOlc(entry) { return { silver: entry.silver || 0, bronze: entry.bronze || 0 }; }

  function entryDeviceCount(entry) { const o = getOlc(entry); return o.silver + o.bronze; }
  function totalDeviceCount() { return state.selectedAwards.reduce((s, e) => s + entryDeviceCount(e), 0); }

  // Device-attachment FEE units: 1 fee per device TYPE present on a ribbon
  // (any bronze = 1, any silver = 1), counted per award and summed across the
  // rack. So 1–4 bronze on a ribbon = 1 fee; bronze + silver on a ribbon = 2.
  function entryDeviceFeeUnits(entry) { const o = getOlc(entry); return (o.silver > 0 ? 1 : 0) + (o.bronze > 0 ? 1 : 0); }
  function totalDeviceFeeUnits() { return state.selectedAwards.reduce((s, e) => s + entryDeviceFeeUnits(e), 0); }

  function entryDevicePrice(entry) {
    const o = getOlc(entry);
    return devicePriceFor('silver', o.silver) + devicePriceFor('bronze', o.bronze);
  }

  function entryDeviceLabel(entry) {
    const o = getOlc(entry); const parts = [];
    if (o.silver > 0) parts.push(o.silver + ' Silver OLC');
    if (o.bronze > 0) parts.push(o.bronze + ' Bronze OLC');
    return parts.join(', ');
  }

  /** Build overlay image array — silver first, then bronze */
  function getOverlays(entry) {
    const o = getOlc(entry); const overlays = [];
    if (silverOlc && silverOlc.image) for (let i = 0; i < o.silver; i++) overlays.push(silverOlc.image);
    if (bronzeOlc && bronzeOlc.image) for (let i = 0; i < o.bronze; i++) overlays.push(bronzeOlc.image);
    return overlays;
  }

  function getAssemblyTier(n) { const t = config.assembly_fee_tiers; if (!t || !t.length) return null; if (n <= (config.assembly_tier1_max || 6) && t[0]) return t[0]; if (n <= (config.assembly_tier2_max || 13) && t[1]) return t[1]; return t[2] || t[t.length - 1]; }

  function isSpacedRack() {
    if (!state.selectedRack) return false;
    const t = state.selectedRack.title.toLowerCase();
    return t.includes('space') || t.includes('1/8');
  }

  /** Position device overlays — clustered together in center of award */
  function positionOverlays(slot, overlays, sizePercent) {
    const count = overlays.length; if (!count) return;
    const devH = sizePercent[count] || sizePercent[4] || 44;
    const aspectRatio = 168 / 400;
    const devW = devH * aspectRatio;

    const offsets = { 1: 0, 2: 16, 3: 13, 4: 11 };
    const offset = offsets[count] || 11;

    const clusterWidth = (count - 1) * offset + devW;
    const startLeft = (100 - clusterWidth) / 2;

    overlays.forEach((src, oi) => {
      const ov = document.createElement('img');
      ov.src = src; ov.alt = '';
      ov.className = slot.className.includes('mini') ? 'uab__mini-preview-device' : 'uab__preview-device-overlay';
      ov.loading = 'lazy';
      ov.style.width = devW + '%';
      ov.style.height = devH + '%';
      ov.style.top = ((100 - devH) / 2) + '%';
      ov.style.left = (startLeft + oi * offset) + '%';
      ov.style.zIndex = String(oi + 2);
      slot.appendChild(ov);
    });
  }

  // ── State ─────────────────────────────────────────────
  const state = {
    currentStep: 1, selectedRackSize: null, selectedRack: null, selectedVariant: null,
    selectedAwards: [], buildId: null, configuringAwardId: null
  };

  // ── Navigation ────────────────────────────────────────
  function goToStep(step) {
    state.currentStep = step;
    document.querySelectorAll('.uab__progress-step').forEach(el => { const s = +el.dataset.step; el.classList.toggle('uab__progress-step--active', s <= step); el.classList.toggle('uab__progress-step--current', s === step); });
    document.querySelectorAll('.uab__step').forEach(el => { el.style.display = +el.dataset.stepPanel === step ? '' : 'none'; });
    document.getElementById('unit-award-builder').scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (step === 2) { updateCounter(); renderAwardList(); renderSummary(); renderPreview(); }
    if (step === 3) renderReview();
  }

  // ═══════════════════════════════════════════════════════
  // STEP 1 — Rack Selection (size → style)
  // ═══════════════════════════════════════════════════════
  function initStep1() {
    const g = document.getElementById('uab-rack-size-grid'); g.innerHTML = '';
    if (!availableSizes.length) { g.innerHTML = '<p style="color:#C0392B">No racks found.</p>'; return; }
    availableSizes.forEach(sz => {
      const b = document.createElement('button'); b.className = 'uab__size-btn'; b.textContent = sz; b.dataset.size = sz;
      b.addEventListener('click', () => {
        state.selectedRackSize = sz; state.selectedRack = null; state.selectedVariant = null;
        document.querySelectorAll('.uab__size-btn').forEach(x => x.classList.toggle('uab__size-btn--active', +x.dataset.size === sz));
        if (state.selectedAwards.length > sz) state.selectedAwards.length = sz;
        document.getElementById('uab-rack-summary').style.display = 'none';
        document.getElementById('uab-step1-next').disabled = true;
        renderRackStyles();
      });
      g.appendChild(b);
    });
  }

  function renderRackStyles() {
    const sz = state.selectedRackSize;
    const racks = racksBySize[sz] || [];
    const c = document.getElementById('uab-rack-variants'), g = document.getElementById('uab-rack-variant-grid');
    g.innerHTML = '';

    if (!racks.length) { g.innerHTML = '<p>No racks available for this size.</p>'; c.style.display = ''; return; }

    if (racks.length === 1) {
      c.style.display = 'none';
      pickRack(racks[0]);
      return;
    }

    g.classList.add('uab__rack-variant-grid--stacked');
    racks.forEach(rack => {
      const v = rack.variants.find(v => v.available);
      if (!v) return;
      const b = document.createElement('button');
      b.className = 'uab__style-btn';
      b.dataset.rackId = rack.id;
      const title = rack.title;
      b.innerHTML = '<span class="uab__style-btn-name">' + esc(title) + '</span><span class="uab__style-btn-price">' + fmt(v.price) + '</span>';
      b.addEventListener('click', () => pickRack(rack));
      g.appendChild(b);
    });
    c.style.display = '';
  }

  function pickRack(rack) {
    state.selectedRack = rack;
    const v = rack.variants.find(v => v.available);
    state.selectedVariant = v || null;

    document.getElementById('uab-rack-variants').style.display = 'none';

    document.getElementById('uab-rack-summary-img').src = rack.image || '';
    document.getElementById('uab-rack-summary-title').textContent = rack.title;
    document.getElementById('uab-rack-summary-variant').textContent = v ? fmt(v.price) : 'Out of stock';
    document.getElementById('uab-rack-summary-price').textContent = '';
    document.getElementById('uab-rack-summary').style.display = '';
    document.getElementById('uab-step1-next').disabled = !v;
  }

  // ═══════════════════════════════════════════════════════
  // STEP 2 — Award List + Device Dropdowns + Live Preview
  // ═══════════════════════════════════════════════════════
  function renderAwardList() {
    const list = document.getElementById('uab-award-list'); list.innerHTML = '';
    const max = state.selectedRackSize || 0, atCap = state.selectedAwards.length >= max;

    awardsSorted.forEach(award => {
      const entry = findEntry(award.id);
      const added = !!entry, dis = atCap && !added;
      const isConfig = state.configuringAwardId === award.id && added;
      const eligible = isOlcEligible(award);

      let badgeText = '+ Add';
      if (added) {
        const s = entry.silver || 0, b = entry.bronze || 0;
        if (s + b > 0) badgeText = '✓ ' + [s ? s + 'S' : '', b ? b + 'B' : ''].filter(Boolean).join(' ');
        else badgeText = '✓ Added';
      }

      const row = document.createElement('button');
      row.className = 'uab__award-item' + (dis ? ' uab__award-item--disabled' : '') + (added ? ' uab__award-item--added' : '') + (isConfig ? ' uab__award-item--config' : '');
      row.disabled = dis;
      row.innerHTML = '<img src="' + (award.image || '') + '" alt="" class="uab__award-item-img" />' +
        '<div class="uab__award-item-info"><span class="uab__award-item-name">' + esc(award.title) + '</span></div>' +
        '<span class="uab__award-item-price">' + fmt(award.price) + '</span>' +
        (added ? '<span class="uab__award-badge uab__award-badge--added">' + badgeText + '</span>' : '<span class="uab__award-badge uab__award-badge--add">' + badgeText + '</span>');
      row.addEventListener('click', () => handleAwardClick(award));
      list.appendChild(row);

      // ── Inline device picker (two dropdowns: silver + bronze) ──
      if (isConfig && entry && eligible) {
        const s = entry.silver || 0, b = entry.bronze || 0;
        const devLabel = entryDeviceLabel(entry);
        const devPrice = entryDevicePrice(entry);

        // Each dropdown is capped so silver + bronze never exceeds MAX_DEVICES.
        function optsFor(current, otherCount) {
          const maxN = MAX_DEVICES - otherCount;
          let o = '';
          for (let n = 0; n <= maxN; n++) {
            o += '<option value="' + n + '"' + (n === current ? ' selected' : '') + '>' + (n === 0 ? 'None' : n) + '</option>';
          }
          return o;
        }
        const silverImg = silverOlc && silverOlc.image ? silverOlc.image : '';
        const bronzeImg = bronzeOlc && bronzeOlc.image ? bronzeOlc.image : '';

        const panel = document.createElement('div');
        panel.className = 'uab__inline-config';
        panel.innerHTML = '<div class="uab__inline-config-inner">' +
          '<div class="uab__inline-config-header"><span>Add devices to this ribbon</span><button class="uab__inline-close" data-close>&times;</button></div>' +
          '<div class="uab__dev-grid">' +
            '<div class="uab__dev-col">' +
              (silverImg ? '<img src="' + silverImg + '" alt="" class="uab__dev-icon" />' : '') +
              '<span class="uab__dev-label">Silver Oak Leaf Clusters</span>' +
              '<select class="uab__times-select uab__dev-select" data-aid="' + award.id + '" data-color="silver">' + optsFor(s, b) + '</select>' +
            '</div>' +
            '<div class="uab__dev-col">' +
              (bronzeImg ? '<img src="' + bronzeImg + '" alt="" class="uab__dev-icon" />' : '') +
              '<span class="uab__dev-label">Bronze Oak Leaf Clusters</span>' +
              '<select class="uab__times-select uab__dev-select" data-aid="' + award.id + '" data-color="bronze">' + optsFor(b, s) + '</select>' +
            '</div>' +
          '</div>' +
          (devLabel ? '<div class="uab__times-result"><span class="uab__times-result-label">Devices:</span> <strong>' + esc(devLabel) + '</strong>' + (devPrice > 0 ? ' — ' + fmt(devPrice) : '') + '</div>' : '') +
          '<div class="uab__times-preview" id="uab-times-preview-' + award.id + '"></div>' +
          '<div class="uab__inline-footer"><button class="uab__inline-remove" data-aid="' + award.id + '">Remove Award</button><button class="uab__inline-done" data-close>Done</button></div>' +
          '</div>';
        list.appendChild(panel);

        renderMiniPreview(entry, 'uab-times-preview-' + award.id);

        panel.querySelectorAll('.uab__dev-select').forEach(sel => sel.addEventListener('change', function () {
          const color = this.getAttribute('data-color');
          const val = +this.value;
          if (color === 'silver') entry.silver = val; else entry.bronze = val;
          renderAwardList(); renderSummary(); renderPreview();
        }));
        panel.querySelectorAll('[data-close]').forEach(x => x.addEventListener('click', (e) => { e.stopPropagation(); state.configuringAwardId = null; renderAwardList(); }));
        panel.querySelectorAll('.uab__inline-remove').forEach(x => x.addEventListener('click', (e) => { e.stopPropagation(); removeAward(+x.dataset.aid); }));

        setTimeout(() => panel.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      }
    });
  }

  function renderMiniPreview(entry, containerId) {
    const container = document.getElementById(containerId); if (!container) return;
    container.innerHTML = '';
    const slot = document.createElement('div'); slot.className = 'uab__mini-preview-slot';
    const img = document.createElement('img'); img.src = entry.award.image; img.alt = entry.award.title; img.className = 'uab__mini-preview-img';
    slot.appendChild(img);
    positionOverlays(slot, getOverlays(entry), { 1: 50, 2: 45, 3: 40, 4: 36 });
    container.appendChild(slot);
  }

  function handleAwardClick(award) {
    const existing = findEntry(award.id);
    if (existing) {
      state.configuringAwardId = (state.configuringAwardId === award.id) ? null : (isOlcEligible(award) ? award.id : null);
      renderAwardList(); return;
    }
    const max = state.selectedRackSize || 0;
    if (state.selectedAwards.length >= max) return;
    state.selectedAwards.push({ award, silver: 0, bronze: 0 });
    state.selectedAwards.sort((a, b) => a.award.precedence - b.award.precedence);
    state.configuringAwardId = isOlcEligible(award) ? award.id : null;
    renderAwardList(); renderSummary(); renderPreview(); updateCounter(); updateNext();
  }

  function removeAward(awardId) {
    const idx = state.selectedAwards.findIndex(e => e.award.id === awardId);
    if (idx >= 0) state.selectedAwards.splice(idx, 1);
    if (state.configuringAwardId === awardId) state.configuringAwardId = null;
    renderAwardList(); renderSummary(); renderPreview(); updateCounter(); updateNext();
  }

  function updateCounter() { const el = document.getElementById('uab-award-counter'), m = state.selectedRackSize || 0, c = state.selectedAwards.length; el.textContent = c + ' / ' + m + ' awards'; el.classList.toggle('uab__counter--full', c >= m); }
  function updateNext() { document.getElementById('uab-step2-next').disabled = !state.selectedAwards.length; }

  // ═══════════════════════════════════════════════════════
  // LIVE VISUAL RACK PREVIEW
  // ═══════════════════════════════════════════════════════
  function renderPreview() {
    const frame = document.getElementById('uab-preview-frame');
    const emptyMsg = document.getElementById('uab-preview-empty');
    frame.innerHTML = '';

    const awards = state.selectedAwards, totalSlots = state.selectedRackSize || 0;
    const spaced = isSpacedRack();
    frame.classList.toggle('uab__preview-frame--spaced', spaced);
    frame.classList.toggle('uab__preview-frame--tight', !spaced);

    if (!awards.length) { emptyMsg.style.display = ''; return; }
    emptyMsg.style.display = 'none';

    const totalRows = Math.ceil(totalSlots / AWARDS_PER_ROW);
    const topRowCount = totalSlots - (totalRows - 1) * AWARDS_PER_ROW;

    let awardIdx = 0;
    for (let row = 0; row < totalRows; row++) {
      const slotsInRow = (row === 0) ? topRowCount : AWARDS_PER_ROW;
      const rowEl = document.createElement('div'); rowEl.className = 'uab__preview-row';

      for (let col = 0; col < slotsInRow; col++) {
        const slot = document.createElement('div'); slot.className = 'uab__preview-slot';

        if (awardIdx < awards.length) {
          const entry = awards[awardIdx];
          slot.classList.add('uab__preview-slot--filled');
          const img = document.createElement('img'); img.src = entry.award.image; img.alt = entry.award.title; img.className = 'uab__preview-award-img'; img.loading = 'lazy';
          slot.appendChild(img);
          positionOverlays(slot, getOverlays(entry), { 1: 50, 2: 45, 3: 40, 4: 36 });
          const dl = entryDeviceLabel(entry);
          slot.title = entry.award.title + (dl ? ' (' + dl + ')' : '');
        } else {
          slot.classList.add('uab__preview-slot--empty');
        }
        rowEl.appendChild(slot); awardIdx++;
      }
      frame.appendChild(rowEl);
    }
  }

  // ── Summary (compact bottom list) ─────────────────────
  function renderSummary() {
    const container = document.getElementById('uab-selected');
    const empty = document.getElementById('uab-empty-state'), hdr = document.getElementById('uab-selected-header');
    container.querySelectorAll('.uab__summary-item').forEach(el => el.remove());
    if (!state.selectedAwards.length) { if (empty) empty.style.display = ''; if (hdr) hdr.style.display = 'none'; return; }
    if (empty) empty.style.display = 'none'; if (hdr) hdr.style.display = '';
    state.selectedAwards.forEach((entry, idx) => {
      const a = entry.award, devLabel = entryDeviceLabel(entry);
      const hasDev = (entry.silver || 0) + (entry.bronze || 0) > 0;
      const el = document.createElement('div'); el.className = 'uab__summary-item';
      el.innerHTML = '<span class="uab__summary-pos">#' + (idx + 1) + '</span><img src="' + (a.image || '') + '" alt="" class="uab__summary-img" /><span class="uab__summary-name">' + esc(a.title) + '</span>' +
        (hasDev ? '<span class="uab__summary-devs">' + esc(devLabel) + '</span>' : '') +
        '<span class="uab__summary-price">' + fmt(a.price) + '</span><button class="uab__summary-remove" data-aid="' + a.id + '">&times;</button>';
      container.appendChild(el);
    });
    container.querySelectorAll('.uab__summary-remove').forEach(b => b.addEventListener('click', () => removeAward(+b.dataset.aid)));
  }

  // ═══════════════════════════════════════════════════════
  // STEP 3 — Review
  // ═══════════════════════════════════════════════════════
  function renderReview() {
    const rp = document.getElementById('uab-review-preview');
    rp.innerHTML = ''; rp.appendChild(document.getElementById('uab-preview-frame').cloneNode(true));
    document.getElementById('uab-review-rack').innerHTML = '<div class="uab__review-line"><span>' + esc(state.selectedRack.title) + '</span><span>' + fmt(state.selectedVariant.price) + '</span></div>';
    let html = '';
    state.selectedAwards.forEach((entry, idx) => {
      const devLabel = entryDeviceLabel(entry), devPrice = entryDevicePrice(entry);
      html += '<div class="uab__review-line"><span>#' + (idx + 1) + ' — ' + esc(entry.award.title) + '</span><span>' + fmt(entry.award.price) + '</span></div>';
      if (devLabel) html += '<div class="uab__review-line uab__review-line--indent"><span>↳ ' + esc(devLabel) + '</span><span>' + fmt(devPrice) + '</span></div>';
    });
    document.getElementById('uab-review-awards').innerHTML = html;
    const dfu = totalDeviceFeeUnits(), rc = state.selectedAwards.length, tier = getAssemblyTier(rc);
    let fh = '';
    if (tier) fh += '<div class="uab__review-line"><span>Assembly — ' + esc(tier.title) + '</span><span>' + fmt(tier.price) + '</span></div>';
    if (config.device_fee_variant_id && dfu > 0) fh += '<div class="uab__review-line"><span>Device Attachment × ' + dfu + '</span><span>' + fmt(config.device_fee_price * dfu) + '</span></div>';
    document.getElementById('uab-review-fees').innerHTML = fh || '<div class="uab__review-line"><span>No additional fees</span><span>—</span></div>';
    document.getElementById('uab-review-total').textContent = fmt(calcTotal());
  }

  function calcTotal() {
    let t = state.selectedVariant ? state.selectedVariant.price : 0;
    state.selectedAwards.forEach(e => { t += e.award.price + entryDevicePrice(e); });
    const tier = getAssemblyTier(state.selectedAwards.length); if (tier) t += tier.price;
    const dfu = totalDeviceFeeUnits(); if (config.device_fee_variant_id && dfu > 0) t += config.device_fee_price * dfu;
    return t;
  }

  // ═══════════════════════════════════════════════════════
  // Add to Cart
  // ═══════════════════════════════════════════════════════
  async function addToCart() {
    const btn = document.getElementById('uab-add-to-cart'); btn.disabled = true; btn.textContent = 'Adding to cart...';
    state.buildId = genId();
    const rc = state.selectedAwards.length, dfu = totalDeviceFeeUnits(), tier = getAssemblyTier(rc);

    const bundleTitle = 'Custom Unit Award Rack — ' + rc + (rc === 1 ? ' Award' : ' Awards') +
      (state.selectedRack ? ' (' + state.selectedRack.title + ')' : '');

    const summary = state.selectedAwards.map((entry, idx) => {
      let line = (idx + 1) + '. ' + entry.award.title;
      const dl = entryDeviceLabel(entry); if (dl) line += ' [' + dl + ']';
      return line;
    });

    const items = [];

    // 1) Rack
    items.push({ id: state.selectedVariant.id, quantity: 1, properties: {
      '_Build ID': state.buildId, '_Build Type': 'Unit Award', 'Assembly': 'Yes',
      'Configuration': rc + ' Awards — ' + state.selectedRack.title,
      'Build Summary': summary.join(' | ')
    }});

    // 2) Awards + 3) OLC device SET products (1 set per ribbon, per color)
    state.selectedAwards.forEach((entry, idx) => {
      const olc = getOlc(entry), devStrs = [];

      if (olc.silver > 0) {
        devStrs.push(olc.silver + ' Silver OLC');
        deviceCartItems('silver', olc.silver, idx, entry.award.title).forEach(it => items.push(it));
      }
      if (olc.bronze > 0) {
        devStrs.push(olc.bronze + ' Bronze OLC');
        deviceCartItems('bronze', olc.bronze, idx, entry.award.title).forEach(it => items.push(it));
      }

      const props = { '_Build ID': state.buildId, 'Rack Position': '#' + (idx + 1) + ' of ' + rc };
      if (devStrs.length) props['Devices'] = devStrs.join(', ');
      items.push({ id: entry.award.variant_id, quantity: 1, properties: props });
    });

    // 4) Assembly fee
    if (tier) items.push({ id: tier.variant_id, quantity: 1, properties: { '_Build ID': state.buildId, '_fee_item': 'true', '_Tier': tier.title } });

    // 5) Device fee
    if (config.device_fee_variant_id && dfu > 0) items.push({ id: config.device_fee_variant_id, quantity: dfu, properties: { '_Build ID': state.buildId, '_fee_item': 'true', '_Service': 'Device attachment' } });

    // Stamp every line with the same _Build ID + _Bundle Title so the cart can
    // collapse the build into one clean line (cart-clean script or function).
    items.forEach(it => {
      it.properties = it.properties || {};
      it.properties['_Build ID'] = state.buildId;
      it.properties['_Bundle Title'] = bundleTitle;
    });

    try {
      const resp = await fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }) });
      if (!resp.ok) { const e = await resp.json(); throw new Error(e.description || e.message || 'Failed'); }
      document.getElementById('uab-cart-feedback').style.display = ''; btn.textContent = 'Added to Cart ✓';
      try { const c = await (await fetch('/cart.js')).json(); document.querySelectorAll('.cart-count, .cart-count-bubble, [data-cart-count]').forEach(el => { el.textContent = c.item_count; }); } catch (_) {}
    } catch (err) { alert('Error: ' + err.message); btn.disabled = false; btn.textContent = 'Add Complete Rack to Cart'; }
  }

  // ── Init ──────────────────────────────────────────────
  function init() {
    initStep1();
    document.getElementById('uab-step1-next').addEventListener('click', () => goToStep(2));
    document.getElementById('uab-step2-back').addEventListener('click', () => goToStep(1));
    document.getElementById('uab-step2-next').addEventListener('click', () => goToStep(3));
    document.getElementById('uab-step3-back').addEventListener('click', () => goToStep(2));
    document.getElementById('uab-add-to-cart').addEventListener('click', addToCart);
    document.getElementById('uab-rack-change').addEventListener('click', () => {
      document.getElementById('uab-rack-summary').style.display = 'none';
      renderRackStyles();
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
})();
